import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ListBulletIcon,
  ChatBubbleBottomCenterIcon,
  ChartBarIcon,
} from "@heroicons/react/24/outline";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import PageLoading from "../../components/PageLoading";
import CommentSection from "../../components/CommentSection";
import { GetRapperProfile } from "../../api-requests/Get";
import { Helmet } from "react-helmet-async";
import RapperHero from "./components/RapperHero";
import RapperStatBar from "./components/RapperStatBar";
import RapperTabs from "./components/RapperTabs";
import OverviewTab from "./components/OverviewTab";
import ListsTab from "./components/ListsTab";
import DebatesTab from "./components/DebatesTab";
import RelatedRappers from "./components/RelatedRappers";
import { ArtistMetadata } from "../../types/metadata";

interface RapperData {
  id: string;
  slug: string;
  name: string;
  imagePath: string | null;
  shortBio: string | null;
  stats: {
    communityRank: number | null;
    listAppearances: number;
    averageRank: number | null;
    totalReactions: number;
    totalComments: number;
    debateCount: number;
    points: number;
  };
  topLists: Array<{
    id: string;
    title: string;
    creator: string;
    creatorId: string;
    rank: number;
    reactions: number;
    comments: number;
  }>;
  debates: Array<{
    id: string;
    title: string;
    voteCount: number;
    isActive: boolean;
  }>;
  relatedRappers: Array<{
    id: string;
    slug: string;
    name: string;
    imagePath: string | null;
    communityRank: number | null;
  }>;
  metadata: ArtistMetadata | null;
}

const RapperPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [rapperData, setRapperData] = useState<RapperData | null>(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const tabs = [
    {
      id: "overview",
      label: "Overview",
      icon: ChartBarIcon,
    },
    {
      id: "lists",
      label: "Lists",
      icon: ListBulletIcon,
      count: rapperData?.stats.listAppearances || 0,
    },
    {
      id: "debates",
      label: "Debates",
      icon: ChatBubbleBottomCenterIcon,
      count: rapperData?.stats.debateCount || 0,
    },
  ];

  useEffect(() => {
    const fetchRapperData = async () => {
      if (!slug) return;

      try {
        setLoading(true);
        setError(null);
        const response = await GetRapperProfile(slug);

        if (response.success) {
          setRapperData(response.data);
        } else {
          setError("Rapper not found");
        }
      } catch (err) {
        setError("Failed to load rapper profile");
        console.error("Error fetching rapper profile:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchRapperData();
  }, [slug]);

  if (loading) {
    return (
      <>
        <Header />
        <PageLoading />
        <Footer />
      </>
    );
  }

  if (error || !rapperData) {
    return (
      <>
        <Header />
        <div className="max-w-4xl mx-auto p-6">
          <div className="text-center py-16">
            <h2 className="text-2xl font-bold text-white mb-4">
              {error || "Rapper Not Found"}
            </h2>
            <p className="text-gray-400 mb-6">
              The rapper profile you're looking for doesn't exist or has been
              removed.
            </p>
            <button
              onClick={() => navigate("/")}
              className="bg-blue-600 text-white px-6 py-3 rounded-md font-medium hover:bg-blue-700"
            >
              Go Home
            </button>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case "overview":
        return (
          <OverviewTab rapperData={rapperData} metadata={rapperData.metadata} />
        );
      case "lists":
        return <ListsTab lists={rapperData.topLists} />;
      case "debates":
        return <DebatesTab debates={rapperData.debates} />;
      default:
        return <OverviewTab rapperData={rapperData} />;
    }
  };

  return (
    <>
      <Helmet>
        <title>{rapperData.name} Rankings, Lists, and Debates | 10GOATS</title>
        <meta
          name="description"
          content={`See ${rapperData.name}'s community ranking, lists, debates, and discussion on 10GOATS.`}
        />
      </Helmet>

      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Hero Section */}
        <RapperHero rapperData={rapperData} />

        {/* Stats Bar */}
        <RapperStatBar stats={rapperData.stats} />

        {/* Tabs Navigation */}
        <RapperTabs
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />

        {/* Tab Content */}
        <div className="mt-8">{renderTabContent()}</div>

        {/* Related Rappers */}
        {rapperData.relatedRappers.length > 0 && (
          <div className="mt-12">
            <RelatedRappers rappers={rapperData.relatedRappers} />
          </div>
        )}

        {/* Comments Section */}
        <div className="mt-12">
          <CommentSection targetType="list_item" targetId={rapperData.id} />
        </div>
      </div>

      <Footer />
    </>
  );
};

export default RapperPage;
